import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Copy, Megaphone, Film, Mail, CheckCircle2, Clock, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

function copy(text, label) {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied — go paste it!`);
}

const PLATFORM_COLOR = {
  "Reddit": "bg-orange-50 border-orange-300 text-orange-700",
  "Facebook Group": "bg-blue-50 border-blue-300 text-blue-700",
  "LinkedIn": "bg-sky-50 border-sky-300 text-sky-700",
  "Shorts/Reels/TikTok": "bg-purple-50 border-purple-300 text-purple-700",
  "B2B Email": "bg-emerald-50 border-emerald-300 text-emerald-700",
};

export default function InstructorPlaybook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("community");

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/playbook");
        setData(d);
      } catch {
        toast.error("Couldn't load playbook");
      } finally { setLoading(false); }
    })();
  }, []);

  const tabs = useMemo(() => [
    { id: "community", label: "Community Posts", icon: Megaphone, count: data?.community_posts?.length || 0 },
    { id: "shorts", label: "Short Scripts", icon: Film, count: data?.short_scripts?.length || 0 },
    { id: "emails", label: "Cold Emails", icon: Mail, count: data?.email_templates?.length || 0 },
  ], [data]);

  if (loading) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading playbook…</div></div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="playbook-page">
        <Link to="/instructor/launch" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-red-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Mission control
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Daily playbook</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter mb-3">Marketing playbook</h1>
        <p className="text-sm text-zinc-700 max-w-2xl leading-relaxed mb-8">
          Phone-friendly. Tap any post → Copy → paste straight into Reddit / Facebook / LinkedIn / TikTok / Gmail.
          Templates are ready for Week 1; AI generates fresh ones daily at <Link to="/instructor/ai-drafts" className="text-red-600 underline">AI Drafts</Link>.
        </p>

        {/* Quick daily-routine reminder card */}
        <div className="card-tech p-4 mb-6" data-testid="playbook-routine">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-red-600" />
            <h3 className="font-heading text-base font-bold tracking-tight">Today's 75-min routine</h3>
          </div>
          <ol className="text-xs text-zinc-700 space-y-1 leading-relaxed list-decimal pl-5">
            <li><strong>9 AM</strong> — 1 community post (rotate Reddit / FB / LinkedIn) → reply to comments for 2 hours</li>
            <li><strong>10 AM</strong> — Film + post 1 Short to YouTube + Instagram + TikTok</li>
            <li><strong>10:30 AM</strong> — Add 5 prospects at <Link to="/instructor/corporate" className="text-red-600">/instructor/corporate</Link> + send 5 cold emails</li>
            <li><strong>12 PM</strong> — Reply to all morning DMs + comments</li>
            <li><strong>5 PM</strong> — Tee up tomorrow's 5 prospects</li>
          </ol>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-5 border-b border-zinc-200">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                  active ? "text-red-600 border-b-2 border-red-600 -mb-px" : "text-zinc-600 hover:text-red-600"
                }`}
                data-testid={`playbook-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" /> {t.label} <span className="text-xs font-mono opacity-60">({t.count})</span>
              </button>
            );
          })}
        </div>

        {/* Community posts */}
        {tab === "community" && (
          <ul className="space-y-4" data-testid="community-list">
            {data.community_posts.map((p) => (
              <li key={p.id} className="card-tech p-5" data-testid={`post-${p.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${PLATFORM_COLOR[p.platform]} mb-2`}>
                      {p.platform}
                    </span>
                    <div className="text-xs text-zinc-500 font-mono">{p.subreddit_or_group}</div>
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {p.best_time}</div>
                </div>
                {p.title !== "(No title — FB post body)" && p.title !== "(No title — LinkedIn post body)" && (
                  <h3 className="font-heading text-base font-bold mb-2">{p.title}</h3>
                )}
                <pre className="text-xs text-zinc-700 whitespace-pre-wrap bg-zinc-50 p-3 mb-3 max-h-72 overflow-y-auto border border-zinc-100">
                  {p.body}
                </pre>
                <div className="flex flex-wrap gap-2 items-center">
                  <button onClick={() => copy(p.body, "Post body")} className="btn-primary text-xs" data-testid={`copy-body-${p.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy body
                  </button>
                  {p.title && !p.title.startsWith("(No title") && (
                    <button onClick={() => copy(p.title, "Title")} className="btn-outline text-xs" data-testid={`copy-title-${p.id}`}>
                      <Copy className="w-3.5 h-3.5" /> Copy title
                    </button>
                  )}
                  {p.tip && (
                    <div className="text-[11px] text-zinc-600 flex items-start gap-1.5 ml-auto max-w-md">
                      <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{p.tip}</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Shorts */}
        {tab === "shorts" && (
          <ul className="space-y-4" data-testid="shorts-list">
            {data.short_scripts.map((s) => (
              <li key={s.id} className="card-tech p-5" data-testid={`short-${s.id}`}>
                <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${PLATFORM_COLOR["Shorts/Reels/TikTok"]} mb-2`}>
                  {s.duration}
                </span>
                <h3 className="font-heading text-base font-bold mb-2">{s.title}</h3>
                <p className="text-xs text-zinc-700 italic mb-3">Hook: "{s.hook}"</p>
                <pre className="text-xs text-zinc-700 whitespace-pre-wrap bg-zinc-50 p-3 mb-3 max-h-72 overflow-y-auto border border-zinc-100">
                  {s.script}
                </pre>
                <div className="text-[11px] text-zinc-500 mb-2 font-mono uppercase tracking-widest">Caption + hashtags</div>
                <pre className="text-xs text-zinc-700 bg-zinc-50 p-3 mb-3 border border-zinc-100 whitespace-pre-wrap">
                  {s.caption}
                </pre>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copy(s.script, "Script")} className="btn-primary text-xs" data-testid={`copy-script-${s.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy script
                  </button>
                  <button onClick={() => copy(s.caption, "Caption")} className="btn-outline text-xs" data-testid={`copy-caption-${s.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy caption
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Emails */}
        {tab === "emails" && (
          <ul className="space-y-4" data-testid="emails-list">
            {data.email_templates.map((e) => (
              <li key={e.id} className="card-tech p-5" data-testid={`email-${e.id}`}>
                <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${PLATFORM_COLOR["B2B Email"]} mb-2`}>
                  {e.name}
                </span>
                <div className="mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Subject</div>
                  <div className="text-sm font-semibold">{e.subject}</div>
                </div>
                <pre className="text-xs text-zinc-700 whitespace-pre-wrap bg-zinc-50 p-3 mb-3 max-h-80 overflow-y-auto border border-zinc-100">
                  {e.body}
                </pre>
                <div className="flex flex-wrap gap-2 items-center mb-2">
                  <button onClick={() => copy(e.subject, "Subject")} className="btn-outline text-xs" data-testid={`copy-subject-${e.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy subject
                  </button>
                  <button onClick={() => copy(e.body, "Body")} className="btn-primary text-xs" data-testid={`copy-body-${e.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Copy body
                  </button>
                </div>
                {e.tip && (
                  <div className="text-[11px] text-zinc-600 flex items-start gap-1.5 pt-2 border-t border-zinc-100">
                    <MessageCircle className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" />
                    <span>{e.tip}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
